import React, { useMemo } from 'react';
import { LoadingButton } from '@one-thd/sui-atomic-components';
import { useReturnStore } from '@src/stores/returnStore';
import { APICallState } from '@src/hooks/useMultiCallCreateReturn';
import { ItemToReturn } from '@src/models/returns';
import { RETURN_METHOD } from '@src/constants/general';
import { usePartialReturnRefund } from '@src/hooks/usePartialReturnRefund';
import { useNavigateToReturnSummary } from '@src/hooks/useNavigateToReturnSummary';
import ReturnReviewCard from '../shared/summary-cards/ReturnReviewCard';
import ItemsNotProcessedCard, {
  ReturnItemRow,
} from '../shared/ItemsNotProcessedCard';

interface ReturnCreateErrorStepProps {
  APICallStateProp: APICallState;
  onRetry: () => void;
  /** Backend/SSR code of the failed create call, forwarded to analytics. */
  code?: string;
}

export const ReturnCreateErrorStep = ({
  APICallStateProp,
  onRetry,
  code,
}: ReturnCreateErrorStepProps) => {
  const selectedItems = useReturnStore((state) => state.selectedItems);
  const navigateToReturnSummary = useNavigateToReturnSummary();

  // Separate successful and failed items based on completed API calls
  const { successfulItems, failedItems } = useMemo(() => {
    // Get all successful itemIds from completed calls
    const successfulItemIds = new Set<string>();

    APICallStateProp.completedCalls.forEach((call) => {
      if (call.success && call.data.shipments) {
        // Extract itemIds from all shipments in this successful call
        call.data.shipments.forEach((shipment) => {
          shipment.itemIds.forEach((itemId) => {
            successfulItemIds.add(itemId);
          });
        });
      }
    });

    // Split selectedItems into successful and failed based on itemIds
    // In-store items are always considered successful since they don't go through returnCreate
    const successful: ItemToReturn[] = [];
    const failed: ItemToReturn[] = [];

    selectedItems.forEach((item) => {
      const isInStoreItem = item.returnMethod === RETURN_METHOD.IN_STORE;
      const isInSuccessfulCall = successfulItemIds.has(item.lineItemId);

      if (isInStoreItem || isInSuccessfulCall) {
        successful.push(item);
      } else {
        failed.push(item);
      }
    });

    return { successfulItems: successful, failedItems: failed };
  }, [selectedItems, APICallStateProp.completedCalls]);

  // Use hook to handle refund calculation and button loading state
  const { showButtonLoader, handleContinueClick } = usePartialReturnRefund({
    successfulItems,
    totalSelectedItems: selectedItems.length,
    onFinishReturning: async () => {
      await navigateToReturnSummary(
        APICallStateProp.completedCalls,
        APICallStateProp.hasDuplicateError,
      );
    },
  });

  return (
    <>
      <ItemsNotProcessedCard
        header="Items Not Processed"
        items={failedItems}
        onRetry={onRetry}
        code={code}
      />
      {successfulItems.length > 0 && (
        <ReturnReviewCard header="Items Ready to Return">
          <div className="sui-space-y-6">
            {successfulItems.map((item, index) => (
              <ReturnItemRow key={`${item.lineItemId}-${index}`} item={item} />
            ))}
            <LoadingButton
              fullWidth
              onClick={handleContinueClick}
              loading={showButtonLoader}
            >
              Finish Returning These Items
            </LoadingButton>
          </div>
        </ReturnReviewCard>
      )}
    </>
  );
};
